import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
  fetchGetApplication,
  fetchPutApplication,
  fetchPutState,
  GetStateByStateKeyResponse,
  PutApplicationRequestBody,
} from '@iwoca/lapi-client/edge';
import { useMutation } from '@tanstack/react-query';
import { cloneDeep, set } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';

import { buyerCrossSellFormValidator } from './buyerCrossSellFormValidator';
import { FormContent } from './components/FormContent';
import { useSuspenseGetStateByStateKey } from '../../api/lending/lapiHooks.suspense';
import ChevronLeft from '../../components/svg/ChevronLeft.svg';
import { iwocaTelDisplay } from '../../constants.json';
import { useSuspenseStateKey } from '../../hooks/useStateKey.hook';
import { useSuspenseUpdateTurnover } from '../PayLinkLanding/hooks/useUpdateTurnover.suspense';
import { redirectToMyAccount } from '../Signup/useRedirectToAccountPages';

type TPurpose = NonNullable<
  NonNullable<PutApplicationRequestBody>['data']
>['requested_products'][0]['purpose'];

type TFormValues = {
  amount: string;
  purpose: string;
  turnover: string;
};

const convertFormattedAmountToNumber = (amount: string) => {
  return parseInt(amount.replace(/,/g, ''), 10);
};

export const BuyerCrossSellForm = () => {
  const { stateKey } = useSuspenseStateKey();
  const { state, refetchState } = useSuspenseGetStateByStateKey();
  const { previousTurnover } = useSuspenseUpdateTurnover();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const { mutateAsync: putState } = useMutation({
    mutationFn: (state: GetStateByStateKeyResponse) => {
      if (!stateKey) {
        throw new Error('No statekey provided');
      }

      return fetchPutState({ stateKey, body: state });
    },
    onSuccess: async () => {
      await refetchState();
    },
  });

  const handleSubmit = async (values: TFormValues) => {
    if (!state || !stateKey) return;

    // We are using local state here because react-hook-form's `formState.isSubmitting` cannot cope
    // with the fact that updateTurnover causes state-change (as its a mutation)
    setIsSubmitting(true);

    try {
      const newState = cloneDeep(state);

      set(newState, 'application.company.last_12_months_turnover', {
        amount: convertFormattedAmountToNumber(values.turnover),
        datetime: new Date().toISOString(),
      });

      await putState({ data: newState });

      await fetchPutApplication({
        stateKey: stateKey,
        body: {
          data: {
            requested_products: [
              {
                product_type: 'credit_facility',
                amount: convertFormattedAmountToNumber(values.amount),
                purpose: values.purpose as TPurpose,
              },
            ],
          },
        },
      });

      const { data: application } = await fetchGetApplication({ stateKey });

      tracking.ampli.newLoanApplicationStepSubmitted();
      tracking.ampli.buyerSubmittedXSellApplication({
        application_id: application?.application_id,
      });
      redirectToMyAccount();
    } catch {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  // This regex adds commas as thousand separators so it displays correctly when pre-populating the form.
  const turnover = previousTurnover
    ? String(previousTurnover).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : '';

  const methods = useForm<TFormValues>({
    values: { amount: '', purpose: '', turnover: turnover },
    resolver: yupResolver(buyerCrossSellFormValidator),
  });

  const onSubmit = methods.handleSubmit(handleSubmit);

  return (
    <div className="min-h-[100vh] bg-secondary-95 text-left">
      <div className="mx-auto my-0 min-h-[470px] max-w-[60rem] p-xl">
        <button className="flex cursor-pointer items-center bg-transparent pb-xl text-m text-primary-40 hover:underline">
          <img
            src={ChevronLeft}
            alt=""
            className="mr-s"
            width={18}
            height={18}
          />
          Go Back
        </button>
        <div className="rounded-l border border-secondary-80 bg-structure-100 p-l">
          <div className="pb-l text-2xl font-med">
            Apply for a business loan
          </div>
          <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
              <FormContent isSubmitting={isSubmitting} />
              {submitError && (
                <div className="pt-m text-[#af0d04] *:font-bold">
                  Oops something went wrong. Please try again or give us a call
                  on {iwocaTelDisplay}.
                </div>
              )}
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
